import React, { useCallback, useState, useRef, useEffect } from 'react';
import { GoogleMap, Marker, InfoWindow, DirectionsRenderer } from '@react-google-maps/api';
import { fetchIsochrone, isInsideIsochrone } from '../services/isochroneService';
import { getAddressFromCoordinates } from '../services/geocodingService';
import {
  getSwedishPlaceTypeParameters,
  filterSwedishPlaceResults,
  filterPlacesByActualDistance
} from '../utils/placeSearchTypes';
import { searchNearbyByTypes, walkingDistancesTo } from '../services/placesNearby';
import { searchRadiusFor } from '../constants/walkabilityPresets';

const toLiteral = (loc) => ({
  lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat,
  lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng
});

// Result markers are dots centred on the place, not pins: a pin's head sticks
// out above its anchor and looks like it lies outside the isochrone area.
const MARKER_COLORS = { ok: '#2E8B57', far: '#E0A100' };
const markerIcon = (status) => ({
  path: window.google.maps.SymbolPath.CIRCLE,
  scale: 7,
  fillColor: MARKER_COLORS[status] || MARKER_COLORS.ok,
  fillOpacity: 1,
  strokeColor: '#ffffff',
  strokeWeight: 2
});

// Hide Google's own place icons and labels. Only places the tool has found
// inside the isochrone area may appear on the map.
const MAP_STYLES = [
  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] }
];

const containerStyle = {
  width: '100%',
  height: '500px'
};

const WalkingMap = ({
  center,
  onMarkerPlaced,
  isPlaceMarkerMode,
  onMapLoad,
  selectedTypes,
  onSearchComplete
}) => {
  const [map, setMap] = useState(null);
  const [originMarker, setOriginMarker] = useState(null);
  const [markerAddress, setMarkerAddress] = useState('');
  const [isochronePolygon, setIsochronePolygon] = useState(null);
  const [placeMarkers, setPlaceMarkers] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [directions, setDirections] = useState(null);
  const [showIsochrone] = useState(true);

  // The isochrone request for the current start point. Searches await it, so
  // a search can never be filtered against a missing or outdated area.
  const isochroneRequestRef = useRef(Promise.resolve(null));

  const loadIsochrone = useCallback((location) => {
    setIsochronePolygon(null);
    setPlaceMarkers([]);
    setDirections(null);
    setSelectedPlace(null);
    const request = fetchIsochrone(location).catch((error) => {
      console.error('Error fetching isochrone:', error);
      return null;
    });
    isochroneRequestRef.current = request;
    request.then((isoData) => {
      if (isochroneRequestRef.current === request) setIsochronePolygon(isoData);
    });
    return request;
  }, []);

  const placesServiceRef = useRef(null);
  const directionsServiceRef = useRef(null);

  const onLoad = useCallback((map) => {
    setMap(map);
    placesServiceRef.current = new window.google.maps.places.PlacesService(map);
    directionsServiceRef.current = new window.google.maps.DirectionsService();
    if (onMapLoad) onMapLoad(map, placesServiceRef.current, directionsServiceRef.current);
  }, [onMapLoad]);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const handleMapClick = useCallback(async (e) => {
    if (isPlaceMarkerMode) {
      const location = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng()
      };

      setOriginMarker(location);
      const isochroneRequest = loadIsochrone(location);

      // Get address
      if (placesServiceRef.current) {
        const address = await getAddressFromCoordinates(e.latLng, placesServiceRef.current);
        setMarkerAddress(address);
      }

      await isochroneRequest;

      if (onMarkerPlaced) {
        onMarkerPlaced(location);
      }
    }
  }, [isPlaceMarkerMode, onMarkerPlaced, loadIsochrone]);

  const handleOriginMarkerDrag = useCallback(async (e) => {
    const location = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng()
    };

    setOriginMarker(location);
    // Clears the old area, routes and places right away
    const isochroneRequest = loadIsochrone(location);

    // Get updated address
    if (placesServiceRef.current) {
      const address = await getAddressFromCoordinates(e.latLng, placesServiceRef.current);
      setMarkerAddress(address);
    }

    await isochroneRequest;

    if (onMarkerPlaced) {
      onMarkerPlaced(location);
    }
  }, [onMarkerPlaced, loadIsochrone]);

  // Search for places
  const searchPlaces = useCallback(async () => {
    if (!originMarker || !placesServiceRef.current || selectedTypes.length === 0) {
      return;
    }

    const radius = 550;
    const center = originMarker;

    // Categorize place types
    const schoolTypes = selectedTypes.filter(type =>
      ['preschool', 'primary_school', 'secondary_school', 'school'].includes(type));
    const systembolagetType = selectedTypes.includes('systembolaget') ? ['systembolaget'] : [];
    const otherTypes = selectedTypes.filter(type =>
      !schoolTypes.includes(type) && type !== 'systembolaget');

    let allResults = [];

    // Search Systembolaget
    for (const type of systembolagetType) {
      const results = await searchSystembolaget(center, radius);
      allResults.push(...results);
    }

    // Search school types
    for (const schoolType of schoolTypes) {
      const results = await searchSchoolType(center, radius, schoolType);
      allResults.push(...results);
    }

    // Search other types
    if (otherTypes.length > 0) {
      const results = await searchOtherPlaceTypes(center, radius, otherTypes);
      allResults.push(...results);
    }

    // Remove duplicates
    const uniqueResults = Array.from(
      new Map(allResults.map(place => [place.place_id, place])).values()
    );

    // Process with walking distances
    const processedPlaces = await processPlacesWithDistance(uniqueResults, center);

    // Filter by 10-minute walking time, and never show a place outside the
    // isochrone area drawn on the map
    const isochronePolygon = await isochroneRequestRef.current;
    const filteredPlaces = processedPlaces.filter(place =>
      place.walkingDurationMinutes && place.walkingDurationMinutes <= 10 &&
      (!isochronePolygon || isInsideIsochrone(isochronePolygon, toLiteral(place.geometry.location)))
    );

    setPlaceMarkers(filteredPlaces);

    if (onSearchComplete) {
      onSearchComplete(filteredPlaces);
    }
  }, [originMarker, selectedTypes, onSearchComplete]);

  // Preset search: one Places (New) query per need, one Routes matrix for
  // walking distances, then a verdict per need. Marks every found place on
  // the map (green = within its target distance, yellow = found but farther).
  const searchPreset = useCallback(async (needs) => {
    if (!originMarker || !needs || needs.length === 0) return [];
    const origin = originMarker;
    const isochronePolygon = await isochroneRequestRef.current;

    const perNeed = await Promise.all(needs.map(async (need) => {
      try {
        const places = await searchNearbyByTypes(origin, need.types, searchRadiusFor(need));
        return { need, places, error: null };
      } catch (error) {
        console.error(`Preset search failed for ${need.id}:`, error);
        return { need, places: [], error };
      }
    }));

    // Never show or count a place outside the isochrone area. With the
    // polygon in hand, drop outsiders before asking for walking distances.
    if (isochronePolygon) {
      perNeed.forEach((entry) => {
        entry.places = entry.places.filter((p) => isInsideIsochrone(isochronePolygon, p.geometry.location));
      });
    }

    const byId = new Map();
    perNeed.forEach(({ places }) => places.forEach((p) => byId.set(p.place_id, p)));
    const unique = Array.from(byId.values());
    const distances = await walkingDistancesTo(origin, unique);
    unique.forEach((p) => Object.assign(p, distances.get(p.place_id)));

    // No polygon (the isochrone request failed): fall back to 10 min walking time.
    const allowed = (p) => isochronePolygon || p.walkingDurationMinutes <= 10;

    const markerById = new Map();
    const results = perNeed.map(({ need, places, error }) => {
      const metric = need.crowFlies ? 'straightLineMeters' : 'walkingDistanceMeters';
      const ranked = places
        .map((p) => byId.get(p.place_id))
        .filter(allowed)
        .sort((a, b) => a[metric] - b[metric]);
      const withinTarget = ranked.filter((p) => p[metric] <= need.maxMeters);
      const nearest = ranked[0] || null;
      const status = error ? 'error' : !nearest ? 'missing' : withinTarget.length > 0 ? 'ok' : 'far';

      ranked.forEach((p) => {
        const inTarget = p[metric] <= need.maxMeters;
        const existing = markerById.get(p.place_id);
        if (!existing) {
          markerById.set(p.place_id, { ...p, needLabels: [need.label], markerStatus: inTarget ? 'ok' : 'far' });
        } else {
          existing.needLabels.push(need.label);
          if (inTarget) existing.markerStatus = 'ok';
        }
      });

      return {
        need,
        status,
        nearest,
        places: ranked,
        withinTargetCount: withinTarget.length,
        searchRadius: searchRadiusFor(need)
      };
    });

    setDirections(null);
    setSelectedPlace(null);
    setPlaceMarkers(Array.from(markerById.values()));
    return results;
  }, [originMarker]);

  // Expose search methods to the parent
  useEffect(() => {
    if (window.walkingMapRef) {
      window.walkingMapRef.searchPlaces = searchPlaces;
      window.walkingMapRef.searchPreset = searchPreset;
    }
  }, [searchPlaces, searchPreset]);

  const searchSystembolaget = (center, radius) => {
    return new Promise((resolve) => {
      const request = {
        location: new window.google.maps.LatLng(center.lat, center.lng),
        radius: radius,
        query: 'Systembolaget',
        type: 'store'
      };

      placesServiceRef.current.textSearch(request, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
          const filtered = results.filter(place =>
            place.name === 'Systembolaget' || place.name.startsWith('Systembolaget '));
          resolve(filtered);
        } else {
          resolve([]);
        }
      });
    });
  };

  const searchSchoolType = (center, radius, schoolType) => {
    return new Promise((resolve) => {
      const typeParams = getSwedishPlaceTypeParameters(schoolType);
      if (!typeParams) {
        resolve([]);
        return;
      }

      const allResults = new Map();
      let searchesCompleted = 0;
      const totalSearches = 1 + typeParams.keywords.length;

      // Nearby search
      const nearbyRequest = {
        location: new window.google.maps.LatLng(center.lat, center.lng),
        radius: radius,
        type: typeParams.baseType
      };

      placesServiceRef.current.nearbySearch(nearbyRequest, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
          results.forEach(place => {
            if (!allResults.has(place.place_id)) {
              allResults.set(place.place_id, place);
            }
          });
        }
        searchesCompleted++;
        checkCompletion();
      });

      // Text searches
      typeParams.keywords.forEach(keyword => {
        const textRequest = {
          location: new window.google.maps.LatLng(center.lat, center.lng),
          radius: radius,
          query: keyword
        };

        placesServiceRef.current.textSearch(textRequest, (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
            results.forEach(place => {
              if (!allResults.has(place.place_id)) {
                allResults.set(place.place_id, place);
              }
            });
          }
          searchesCompleted++;
          checkCompletion();
        });
      });

      function checkCompletion() {
        if (searchesCompleted === totalSearches) {
          const resultsArray = Array.from(allResults.values());
          const withinRadius = filterPlacesByActualDistance(resultsArray, center, radius);
          const filtered = filterSwedishPlaceResults(withinRadius, schoolType);
          resolve(filtered);
        }
      }
    });
  };

  const searchOtherPlaceTypes = (center, radius, placeTypes) => {
    return new Promise((resolve) => {
      let allResults = [];
      let completedSearches = 0;

      placeTypes.forEach(placeType => {
        const request = {
          location: new window.google.maps.LatLng(center.lat, center.lng),
          radius: radius,
          type: placeType
        };

        placesServiceRef.current.nearbySearch(request, (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
            allResults.push(...results);
          }
          completedSearches++;

          if (completedSearches === placeTypes.length) {
            resolve(allResults);
          }
        });
      });
    });
  };

  const processPlacesWithDistance = async (places, origin) => {
    const processedPlaces = [];

    for (let place of places) {
      try {
        const placeInfo = {
          ...place,
          name: place.name || 'Unknown',
          vicinity: place.vicinity || place.formatted_address || 'No address',
          rating: place.rating || 'No rating',
          types: place.types || []
        };

        if (place.geometry && place.geometry.location) {
          const routeInfo = await calculateWalkingDistance(origin, place.geometry.location);
          placeInfo.walkingDistance = routeInfo.distance;
          placeInfo.walkingDuration = routeInfo.duration;
          placeInfo.walkingDistanceMeters = routeInfo.distanceValue;
          placeInfo.walkingDurationMinutes = routeInfo.durationValue;
        }

        processedPlaces.push(placeInfo);
      } catch (error) {
        console.error(`Error processing place ${place.name}:`, error);
      }
    }

    return processedPlaces;
  };

  const calculateWalkingDistance = (origin, destination) => {
    return new Promise((resolve) => {
      const request = {
        origin: new window.google.maps.LatLng(origin.lat, origin.lng),
        destination: destination,
        travelMode: window.google.maps.TravelMode.WALKING
      };

      directionsServiceRef.current.route(request, (response, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          const leg = response.routes[0].legs[0];
          resolve({
            distance: leg.distance.text,
            duration: leg.duration.text,
            distanceValue: leg.distance.value,
            durationValue: Math.round(leg.duration.value / 60)
          });
        } else {
          // Fallback to straight-line distance
          const lat1 = origin.lat;
          const lng1 = origin.lng;
          const lat2 = typeof destination.lat === 'function' ? destination.lat() : destination.lat;
          const lng2 = typeof destination.lng === 'function' ? destination.lng() : destination.lng;

          const distance = calculateStraightLineDistance(lat1, lng1, lat2, lng2);
          const duration = Math.round(distance / 83); // 83 m/min walking speed

          resolve({
            distance: `${Math.round(distance)} m`,
            duration: `${duration} min`,
            distanceValue: distance,
            durationValue: duration
          });
        }
      });
    });
  };

  const calculateStraightLineDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371e3;
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lng2 - lng1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  const handlePlaceClick = useCallback((place) => {
    setSelectedPlace(place);

    // Show route
    if (originMarker && place.geometry && place.geometry.location) {
      const request = {
        origin: new window.google.maps.LatLng(originMarker.lat, originMarker.lng),
        destination: place.geometry.location,
        travelMode: window.google.maps.TravelMode.WALKING
      };

      directionsServiceRef.current.route(request, (response, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(response);
        }
      });
    }
  }, [originMarker]);

  // Let the parent's result list open a place on the map (route + info window)
  useEffect(() => {
    if (window.walkingMapRef) {
      window.walkingMapRef.focusPlace = (place) => {
        handlePlaceClick(place);
        if (map && place.geometry && place.geometry.location) {
          map.panTo(place.geometry.location);
        }
      };
    }
  }, [handlePlaceClick, map]);

  // Render isochrone polygon
  useEffect(() => {
    if (!map || !isochronePolygon) return;

    const dataLayer = new window.google.maps.Data();
    dataLayer.addGeoJson(isochronePolygon);
    dataLayer.setStyle({
      fillColor: '#4285F4',
      fillOpacity: 0.3,
      strokeColor: '#4285F4',
      strokeWeight: 2,
      strokeOpacity: 0.8,
      clickable: false
    });

    if (showIsochrone) {
      dataLayer.setMap(map);
    }

    return () => {
      dataLayer.setMap(null);
    };
  }, [map, isochronePolygon, showIsochrone]);

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={15}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onClick={handleMapClick}
      options={{
        styles: MAP_STYLES,
        mapTypeControl: true,
        fullscreenControl: true,
        scrollwheel: true
      }}
    >
      {originMarker && (
        <Marker
          position={originMarker}
          draggable={true}
          onDragEnd={handleOriginMarkerDrag}
          icon={{
            url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png"
          }}
        />
      )}

      {placeMarkers.map((place, index) => {
        const position = place.geometry && place.geometry.location
          ? {
              lat: typeof place.geometry.location.lat === 'function'
                ? place.geometry.location.lat()
                : place.geometry.location.lat,
              lng: typeof place.geometry.location.lng === 'function'
                ? place.geometry.location.lng()
                : place.geometry.location.lng
            }
          : null;

        if (!position) return null;

        return (
          <Marker
            key={`${place.place_id}-${index}`}
            position={position}
            onClick={() => handlePlaceClick(place)}
            icon={markerIcon(place.markerStatus)}
          />
        );
      })}

      {selectedPlace && selectedPlace.geometry && selectedPlace.geometry.location && (
        <InfoWindow
          position={{
            lat: typeof selectedPlace.geometry.location.lat === 'function'
              ? selectedPlace.geometry.location.lat()
              : selectedPlace.geometry.location.lat,
            lng: typeof selectedPlace.geometry.location.lng === 'function'
              ? selectedPlace.geometry.location.lng()
              : selectedPlace.geometry.location.lng
          }}
          onCloseClick={() => setSelectedPlace(null)}
        >
          <div>
            <h3>{selectedPlace.name}</h3>
            {selectedPlace.needLabels && <p><em>{selectedPlace.needLabels.join(', ')}</em></p>}
            <p>{selectedPlace.vicinity}</p>
            <p>Gångavstånd: {selectedPlace.walkingDistance || `${Math.round(selectedPlace.walkingDistanceMeters)} m`}</p>
            <p>Gångtid: {selectedPlace.walkingDuration || `${selectedPlace.walkingDurationMinutes} min`}</p>
            {selectedPlace.rating && selectedPlace.rating !== 'No rating' && <p>Betyg: {selectedPlace.rating} ⭐</p>}
          </div>
        </InfoWindow>
      )}

      {directions && (
        <DirectionsRenderer
          directions={directions}
          options={{
            suppressMarkers: true,
            preserveViewport: true
          }}
        />
      )}
    </GoogleMap>
  );
};

export default WalkingMap;
